import { Spinner } from '@heroui/react';
import { applyActionCode, signOut } from 'firebase/auth';
import { CheckCircle } from 'lucide-react';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { auth } from '@/config';
import { useUserState } from '@/state';
import { useLocaleNavigate } from '@/utils/locale';

import { ReactComponent as Logo } from '../../../assets/logo-hs.svg';
import {
  AuthContainer,
  FormContainer,
  FormWrapper,
  LogoWrapper,
  MarketingContent,
  MarketingDescription,
  MarketingSection,
  MarketingTitle,
  SubmitButton,
  WelcomeSubtitle,
  WelcomeTitle,
} from '../auth.styled';

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${({ theme }) => (theme.mode === 'dark' ? '#14532d' : '#f0fdf4')};
  margin: 0 auto 24px;

  svg {
    width: 48px;
    height: 48px;
    color: ${({ theme }) => (theme.mode === 'dark' ? '#4ade80' : '#16a34a')};
  }
`;

export const VerifyEmailSuccess: FC = () => {
  const { t } = useTranslation();
  const navigate = useLocaleNavigate();
  const { currentUser } = useUserState();
  const [searchParams] = useSearchParams();
  const [countdown, setCountdown] = useState(5);
  const [isVerifying, setIsVerifying] = useState(true);
  const [verificationError, setVerificationError] = useState<string | null>(null);

  const oobCode = searchParams.get('oobCode');

  // Apply email verification code
  useEffect(() => {
    const verifyEmail = async () => {
      if (!oobCode) {
        setVerificationError('Brak kodu weryfikacyjnego');
        setIsVerifying(false);
        return;
      }

      try {
        // Apply the verification code
        await applyActionCode(auth, oobCode);

        // Reload user to get updated emailVerified status
        if (currentUser) {
          await currentUser.reload();
        }

        setIsVerifying(false);
      } catch (error: any) {

        // If user is logged in, check if email is already verified
        if (currentUser && error.code === 'auth/invalid-action-code') {
          await currentUser.reload();
          if (currentUser.emailVerified) {
            // Email is already verified, treat as success
            setIsVerifying(false);
            return;
          }
        }

        // Parse Firebase error message
        let errorMsg = 'Nie udało się zweryfikować emaila';
        if (error.code === 'auth/invalid-action-code') {
          errorMsg = 'Link weryfikacyjny wygasł lub został już użyty';
        } else if (error.code === 'auth/expired-action-code') {
          errorMsg = 'Link weryfikacyjny wygasł';
        }

        setVerificationError(errorMsg);
        setIsVerifying(false);
      }
    };

    verifyEmail();
  }, [oobCode, currentUser]);

  useEffect(() => {
    // Only start countdown after verification is complete
    if (isVerifying || verificationError) return;

    if (countdown === 0) {
      if (currentUser) {
        navigate('/create-organization');
      } else {
        navigate('/login');
      }
      return;
    }

    const timer = setTimeout(() => {
      setCountdown(countdown - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [countdown, currentUser, navigate, isVerifying, verificationError]);

  const handleContinue = () => {
    if (currentUser) {
      navigate('/create-organization');
    } else {
      navigate('/login');
    }
  };

  // Show loading state while verifying
  if (isVerifying) {
    return (
      <AuthContainer>
        <FormContainer>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            width: '100%',
          }}
          >
            <Spinner
              size="lg"
              classNames={{
                circle1: 'border-b-gray-900',
                circle2: 'border-b-gray-900',
              }}
            />
          </div>
        </FormContainer>
        <MarketingSection>
          <MarketingContent>
            <MarketingTitle>{t('auth.verifyEmailSuccess.marketingTitle')}</MarketingTitle>
            <MarketingDescription>
              {t('auth.verifyEmailSuccess.marketingDescription')}
            </MarketingDescription>
          </MarketingContent>
        </MarketingSection>
      </AuthContainer>
    );
  }

  // Show error state
  if (verificationError) {
    return (
      <AuthContainer>
        <FormContainer>
          <FormWrapper>
            <LogoWrapper>
              <Logo />
            </LogoWrapper>
            <WelcomeTitle>Błąd weryfikacji</WelcomeTitle>
            <WelcomeSubtitle>{verificationError}</WelcomeSubtitle>
            <WelcomeSubtitle style={{ marginTop: '16px' }}>
              Zaloguj się ponownie, aby otrzymać nowy link weryfikacyjny.
            </WelcomeSubtitle>

            <SubmitButton
              type="button"
              onClick={async () => {
                await signOut(auth);
                navigate('/login');
              }}
            >
              Zaloguj się
            </SubmitButton>
          </FormWrapper>
        </FormContainer>
        <MarketingSection>
          <MarketingContent>
            <MarketingTitle>{t('auth.verifyEmailSuccess.marketingTitle')}</MarketingTitle>
            <MarketingDescription>
              {t('auth.verifyEmailSuccess.marketingDescription')}
            </MarketingDescription>
          </MarketingContent>
        </MarketingSection>
      </AuthContainer>
    );
  }

  return (
    <AuthContainer>
      <FormContainer>
        <FormWrapper>
          <LogoWrapper>
            <Logo />
          </LogoWrapper>

          <IconWrapper>
            <CheckCircle />
          </IconWrapper>

          <WelcomeTitle>{t('auth.verifyEmailSuccess.title')}</WelcomeTitle>
          <WelcomeSubtitle>
            {t('auth.verifyEmailSuccess.subtitle')}
          </WelcomeSubtitle>

          <SubmitButton type="button" onClick={handleContinue}>
            {currentUser
              ? t('auth.verifyEmailSuccess.continueButton')
              : t('auth.verifyEmailSuccess.loginButton')}
          </SubmitButton>

          <p style={{ textAlign: 'center', marginTop: '16px', fontSize: '14px', color: '#666' }}>
            {t('auth.verifyEmailSuccess.autoRedirect', { seconds: countdown })}
          </p>
        </FormWrapper>
      </FormContainer>

      <MarketingSection>
        <MarketingContent>
          <MarketingTitle>{t('auth.verifyEmailSuccess.marketingTitle')}</MarketingTitle>
          <MarketingDescription>
            {t('auth.verifyEmailSuccess.marketingDescription')}
          </MarketingDescription>
        </MarketingContent>
      </MarketingSection>
    </AuthContainer>
  );
};
